<script setup>
import {
  useHead,
  useRoute,
  useScrollTopOnMounted,
  useSanityQuery,
  computed,
  ref,
  onMounted,
  onUnmounted,
  useImageSource,
  nextTick,
} from '#imports';
import { getCommonMetaTags } from '~/utils/get-common-meta-tags';
import { getQuery as getPrideQuery } from '~/groq-queries/pride';

/* eslint-disable import/no-unresolved */
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Mousewheel, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';

useScrollTopOnMounted();
const route = useRoute();

const title = 'DOXA Прайд';
const description =
  'В июне мы отмечаем месяц гордости, посвященный свободе быть собой для всех представителей ЛГБТКИА+ сообщества. Присоединяйтесь к нашему онлайн-прайду и высказывайтесь на DOXA';

const contentQuery = getPrideQuery();
const { data: content } = await useSanityQuery(contentQuery);
const ogImageUrl = computed(() =>
  useImageSource({
    image: content.value.ogImage,
  })
);

useHead({
  title,
  meta: getCommonMetaTags({
    path: route.fullPath,
    ogType: 'website',
    title,
    description,
    ogImageUrl: ogImageUrl.value,
  }),
});

const allCards = computed(() => content.value.prideSpecialCards);
const swiper = ref();
const modules = [Mousewheel, FreeMode];
const activeSlideIndex = ref();
const likedCards = ref([]);

function setSwiper(swiperInstance) {
  swiper.value = swiperInstance;
}

function extractSlideIndexFromHash(hash) {
  const match = hash.match(/message-(\d+)/);
  return match ? Number.parseInt(match[1], 10) : undefined;
}

const toggleLike = async (index, cardId) => {
  // Проверяем, лайкнута ли уже эта карточка
  if (likedCards.value[index]) {
    // Удаляем лайк
    likedCards.value[index] = false;
  } else {
    // Добавляем лайк
    likedCards.value[index] = true;
  }
  // Обновляем DOM
  await nextTick();

  // Отправляем запрос на сервер
  const requestUrl = likedCards.value[index]
    ? `https://pride-api.doxa.team/like?id=${cardId}`
    : `https://pride-api.doxa.team/like?id=${cardId}&action=remove`;

  try {
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(
        likedCards.value[index]
          ? 'Ошибка сети при попытке лайкнуть карточку'
          : 'Ошибка сети при попытке удалить лайк'
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Ошибка:', error);
  }
};

const updateHeight = () => {
  let lastInnerHeight;
  const vh = window.innerHeight;

  if (vh !== lastInnerHeight) {
    document.documentElement.style.setProperty('--viewport-height', `${vh}px`);
    lastInnerHeight = vh;
  }
};

onMounted(() => {
  window.addEventListener('resize', updateHeight);
  if (CSS.supports('overflow', 'clip')) {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }
  updateHeight();

  const hash = window.location.hash.slice(1);
  if (hash) {
    const slideIndex = extractSlideIndexFromHash(hash);
    activeSlideIndex.value = slideIndex;
    swiper.value.slideToLoop(slideIndex);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateHeight);
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
});
</script>

<template>
  <LayoutStaticPage is-pride is-hide-footer is-transparent-header>
    <div class="pride-page">
      <div class="pride-page__parallax"></div>
      <NuxtLink :to="{ name: 'pride' }" class="pride-page__info">?</NuxtLink>
      <Swiper
        v-if="allCards.length > 0"
        :hash-navigation="{ watchState: true }"
        :space-between="60"
        :modules="modules"
        :direction="'vertical'"
        :slides-per-view="'auto'"
        :free-mode="true"
        :scrollbar="true"
        :mousewheel="true"
        :loop="true"
        :centered-slides="true"
        @swiper="setSwiper"
      >
        <SwiperSlide
          v-for="(card, index) in allCards"
          :key="`message-${index}`"
          :data-hash="`message-${index}`"
          class="pride-page__slide"
          :class="{ 'pride-page__slide--left': index % 2 === 1 }"
        >
          <div v-if="card.image" class="pride-card__avatar">
            <BaseAvatar :image="card.image" size="large" />
          </div>
          <div class="pride-page__card">
            <div class="pride-card__header">
              <h3>{{ card.name }}</h3>
              <div class="pride-card__socials">
                <div
                  class="pride-card__icon pride-card__like"
                  :class="{ 'pride-card__liked': likedCards[index] }"
                  @click="toggleLike(index, card.publishedAt, card.text)"
                ></div>
              </div>
            </div>
            <div class="pride-card__content">
              <p>{{ card.text }}</p>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  </LayoutStaticPage>
</template>

<style lang="postcss" scoped>
.pride-page {
  height: var(--viewport-height);
  display: flex;
  flex-direction: column;
  position: relative;
  background: linear-gradient(
      180deg,
      rgba(116, 255, 171, 0%) 63.85%,
      #74ffab 100%
    ),
    linear-gradient(180deg, #fff6f8 25.83%, #9539a8 83.12%);

  @media (--small-vp) {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  &::before {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    left: 0;
    height: 130px;
    content: '';
    background: linear-gradient(
      180deg,
      rgba(149, 57, 168, 0%) 4.3%,
      rgba(149, 57, 168, 77%) 50.83%,
      #9539a8 100%
    );
    transform: matrix(1, 0, 0, -1, 0, 0);
  }

  &::after {
    position: absolute;
    z-index: 1;
    right: 0;
    bottom: 0;
    left: 0;
    height: 130px;
    content: '';
    background: linear-gradient(
      180deg,
      #74ffab 0%,
      rgba(116, 255, 171, 29%) 56.56%,
      rgba(116, 255, 171, 0%) 100%
    );
    transform: matrix(1, 0, 0, -1, 0, 0);
  }

  &__info {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #74ffab;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: var(--h2-font-size);
    font-weight: 500;
    position: absolute;
    bottom: 40px;
    right: 24px;
    font-family: var(--title-font-family);
  }

  &__parallax {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 918 1878'%3E%3Cpath fill='%23DC4441' d='m512.3 561 5 13-65.3 25.2-5-13.1zM0 1179.7l11-8.7 43.5 54.8-11 8.7z'/%3E%3Cpath fill='%23E47B48' d='m119.7.4 3.1 13.6-68.2 15.7L51.4 16zm786.8 746.5-8 11.5-57.5-40 8-11.4z'/%3E%3Cpath fill='%23F3D446' d='m779 20.7 10-9.7 48.8 50.2-10 9.8zM457.9 1097l.8 14-69.9 4-.8-14zM305 712.7l10-9.7 48.8 50.2-10 9.8zM82 1653.8l10-9.8 48.8 50.3-10 9.7z'/%3E%3Cpath fill='%2335AE44' d='m448.2 41.4-7.7 11.7L382 14.7 389.7 3zm336.9 1731.5-13.7 2.8-14.3-68.5 13.7-2.8zM105.1 802l14 1.3-6.5 69.7-14-1.3zm568.5 287.2-11.7 7.7-38.9-58.2 11.7-7.7z'/%3E%3Cpath fill='%234D4EC4' d='m203.8 369.7 13.3-4.2 21 66.8-13.3 4.2zM858.6 1487l12.5 6.3-31.6 62.5-12.5-6.4zM8.1 1793l14 .2-1 70-14-.2z'/%3E%3Cpath fill='%2383549D' d='m86.5 574 12.9 5.5-27.5 64.4-12.9-5.5zM601 1863.7l2.8-13.7 68.6 13.8-2.8 13.8zm269.9-877.9L883 979l34.3 61-12.2 6.9z'/%3E%3Cpath fill='%23613915' d='m585 1276.5 14-.5 2.4 70-14 .5z'/%3E%3Cpath fill='%23000' d='m674.3 112 8.8 10.8-54.3 44.2-8.8-11zM372.4 1853.4l-9.2 10.5-52.6-46.3 9.3-10.5z'/%3E%3Cpath fill='%23fff' stroke='%23000' d='m546.2 336.7 8.9 9.5-50.5 47-8.9-9.5zM429 1346l-6.5 11.3-59.8-34.4 6.4-11.2zM270.2 931.7l8.9 9.5-50.5 47-8.9-9.5z'/%3E%3Cpath fill='%23FCB1C7' d='m41.8 204 12.4 6.6-32.8 61.8L9 266zM785 1291.4l11.2-8.4 42 56-11.2 8.4zM281.4 1511l10 9.9-49.5 49.6-9.9-10zm623-1075 10 9.9-49.5 49.6-9.9-10z'/%3E%3Cpath fill='%235A3014' d='m676.8 615 12.4 6.6-32.8 61.8L644 677z'/%3E%3Cpath fill='%238FBAEF' d='M232 1200h14v70h-14zm203.7 437 13-5.1 25.6 65.2-13 5zM695.6 307l12.2-6.9 34.7 60.8-12.2 7z'/%3E%3Cpath fill='%235A3014' d='m265 157.5 12.1-7.2 35.9 60.1-12 7.2z'/%3E%3Cpath fill='%23000' d='M533.2 849.1 531 863l-69.1-11.1L464 838z'/%3E%3Cpath fill='%23DC4441' d='m602.2 1496.3 12.8 5.8-28.8 63.8-12.7-5.8z'/%3E%3Cpath fill='%2335AE44' d='m43.8 1418.6-4.1-13.4 66.8-20.9 4.1 13.4z'/%3E%3C/svg%3E");
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 0;
    display: block;
    top: 0;
    bottom: 0;
  }

  .swiper {
    z-index: 0;
    max-width: 600px;
    margin: 0 auto;
    padding: 100px 20px;
  }

  .swiper-wrapper {
    transition-timing-function: linear;
  }

  &__slide {
    height: 200px;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 20px;

    @media (--small-vp) {
      height: fit-content;
    }
  }

  main {
    height: calc(100vh - 56px);
    overflow: hidden;
    position: relative;
  }

  &__cards {
    height: 100%;
    overflow-y: auto;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  &__slide--left {
    flex-direction: row-reverse;
  }

  &__card {
    background-color: #f9f9f9;
    border: 1px solid #000;
    box-shadow: 0 0 0 -1px rgba(0, 0, 0, 100%),
      5px 5px 0 0 rgba(116, 255, 171, 100%);
    padding: 24px;
    height: fit-content;
    position: relative;
    min-width: 232px;

    @media (--x-small-vp) {
      min-width: 180px;
      max-width: 200px;
    }
  }

  .pride-card__content {
    margin-top: 16px;
  }

  .pride-card__socials {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  .pride-card__icon {
    width: 25px;
    height: 25px;
    cursor: pointer;
    outline: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0%);
    user-select: none;
  }

  .pride-card__share {
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 25 25'%3E%3Cpath fill='%23000' fill-rule='evenodd' d='M14.6 6.3a4.2 4.2 0 1 1 1 2.7l-5.3 2.6a4.2 4.2 0 0 1 0 1.8l5.3 2.6s1.9-1.4 3.2-1.4a4.2 4.2 0 1 1-4.1 3.3l-5.3-2.7A4.2 4.2 0 0 1 2 12.5a4.2 4.2 0 0 1 7.3-2.7L14.7 7l-.1-.8Z' clip-rule='evenodd'/%3E%3C/svg%3E");
  }

  .pride-card__like {
    transition: background 0.3s ease-in-out;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 25 25'%3E%3Cg clip-path='url(%23a)'%3E%3Cpath stroke='%23000' stroke-width='3' d='m11.4 5.3 1.1 1 1-1 .8-.7a5.4 5.4 0 0 1 7.6 0 5.4 5.4 0 0 1 0 7.7l-9.4 9.4-9.4-9.4a5.5 5.5 0 0 1 0-7.7 5.4 5.4 0 0 1 7.7 0l.6.7Zm.5 17Z'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M0 0h25v25H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
  }

  .pride-card__liked {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 25 25'%3E%3Cg clip-path='url(%23a)'%3E%3Cpath fill='%23000' d='M23 3.6c-1.3-1.4-3-2-5-2-1.8 0-3.5.6-4.8 2l-.7.6-.7-.7A6.9 6.9 0 0 0 0 8.5c0 1.8.7 3.5 2 4.8l10 10 .5.2c.2 0 .4 0 .5-.2l10-10a6.9 6.9 0 0 0 0-9.7Z'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M0 0h25v25H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
  }

  .pride-card__like:hover {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 25 25'%3E%3Cg clip-path='url(%23a)'%3E%3Cpath fill='%23000' d='M23 3.6c-1.3-1.4-3-2-5-2-1.8 0-3.5.6-4.8 2l-.7.6-.7-.7A6.9 6.9 0 0 0 0 8.5c0 1.8.7 3.5 2 4.8l10 10 .5.2c.2 0 .4 0 .5-.2l10-10a6.9 6.9 0 0 0 0-9.7Z'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M0 0h25v25H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
  }

  .pride-card__liked:hover {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 25 25'%3E%3Cg clip-path='url(%23a)'%3E%3Cpath stroke='%23000' stroke-width='3' d='m11.4 5.3 1.1 1 1-1 .8-.7a5.4 5.4 0 0 1 7.6 0 5.4 5.4 0 0 1 0 7.7l-9.4 9.4-9.4-9.4a5.5 5.5 0 0 1 0-7.7 5.4 5.4 0 0 1 7.7 0l.6.7Zm.5 17Z'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M0 0h25v25H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
  }

  @media (--small-vp) {
    .pride-card__like:hover {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 25 25'%3E%3Cg clip-path='url(%23a)'%3E%3Cpath stroke='%23000' stroke-width='3' d='m11.4 5.3 1.1 1 1-1 .8-.7a5.4 5.4 0 0 1 7.6 0 5.4 5.4 0 0 1 0 7.7l-9.4 9.4-9.4-9.4a5.5 5.5 0 0 1 0-7.7 5.4 5.4 0 0 1 7.7 0l.6.7Zm.5 17Z'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M0 0h25v25H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
    }

    .pride-card__liked:hover {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 25 25'%3E%3Cg clip-path='url(%23a)'%3E%3Cpath fill='%23000' d='M23 3.6c-1.3-1.4-3-2-5-2-1.8 0-3.5.6-4.8 2l-.7.6-.7-.7A6.9 6.9 0 0 0 0 8.5c0 1.8.7 3.5 2 4.8l10 10 .5.2c.2 0 .4 0 .5-.2l10-10a6.9 6.9 0 0 0 0-9.7Z'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M0 0h25v25H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
    }
  }

  .pride-card__avatar {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    box-shadow: 0 0 0 -1px rgba(0, 0, 0, 100%),
      5px 5px 0 0 rgba(116, 255, 171, 100%);
  }

  .pride-card__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
